import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { 
  Users, FolderOpen, Clock, TrendingUp, 
  AlertCircle, CheckCircle, Calendar, ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Students',
      value: stats?.totalStudents || 0,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      subtext: `${stats?.activeStudents || 0} active`
    },
    {
      title: 'Total Projects',
      value: stats?.totalProjects || 0,
      icon: FolderOpen,
      color: 'from-purple-500 to-purple-600',
      subtext: 'All time'
    },
    {
      title: 'New Leads',
      value: stats?.newLeads || 0,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      subtext: `${stats?.totalLeads || 0} total`
    },
    {
      title: 'Active Projects',
      value: stats?.projectsByStatus?.find(s => s._id === 'in-progress')?.count || 0,
      icon: Clock,
      color: 'from-orange-500 to-orange-600',
      subtext: 'In progress'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'on-hold': 'bg-gray-100 text-gray-800',
      'review': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'urgent': <AlertCircle className="w-4 h-4 text-red-500" />,
      'high': <AlertCircle className="w-4 h-4 text-orange-500" />,
      'medium': <Clock className="w-4 h-4 text-yellow-500" />,
      'low': <CheckCircle className="w-4 h-4 text-green-500" />
    };
    return icons[priority] || null;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className={`bg-gradient-to-r ${stat.color} p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">{stat.title}</p>
                    <p className="text-3xl font-bold text-white mt-1">{stat.value}</p>
                  </div>
                  <stat.icon className="w-10 h-10 text-white/50" />
                </div>
              </div>
              <div className="px-4 py-2 bg-gray-50">
                <p className="text-sm text-gray-600">{stat.subtext}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Projects</h3>
              <Link to="/admin/projects" className="text-primary-600 hover:text-primary-700 text-sm flex items-center">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            <div className="space-y-3">
              {stats?.recentProjects?.map((project) => (
                <div key={project._id} className="border-l-4 border-primary-500 pl-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">{project.title}</p>
                      <p className="text-sm text-gray-600">
                        {project.student?.name} • {project.projectId}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.recentProjects || stats.recentProjects.length === 0) && (
                <p className="text-gray-500 text-center py-4">No recent projects</p>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Upcoming Deadlines</h3>
              <Calendar className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {stats?.upcomingDeadlines?.map((project) => (
                <div key={project._id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {getPriorityIcon(project.priority)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{project.title}</p>
                    <p className="text-sm text-gray-600">{project.student?.name}</p>
                    <p className="text-sm text-red-600 mt-1">
                      Due: {format(new Date(project.endDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              ))}
              {(!stats?.upcomingDeadlines || stats.upcomingDeadlines.length === 0) && (
                <p className="text-gray-500 text-center py-4">No upcoming deadlines</p>
              )}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl shadow-lg p-6 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/admin/students" className="bg-white/20 hover:bg-white/30 rounded-lg p-4 text-center transition-colors">
              <Users className="w-8 h-8 mx-auto mb-2" />
              <p>Add New Student</p>
            </Link>
            <Link to="/admin/projects" className="bg-white/20 hover:bg-white/30 rounded-lg p-4 text-center transition-colors">
              <FolderOpen className="w-8 h-8 mx-auto mb-2" />
              <p>Create Project</p>
            </Link>
            <Link to="/admin/leads" className="bg-white/20 hover:bg-white/30 rounded-lg p-4 text-center transition-colors">
              <TrendingUp className="w-8 h-8 mx-auto mb-2" />
              <p>View Leads</p>
            </Link>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;